'use client';

import { formatDurationFromMinutes, isAvailablePriceOption } from '@cfixe-monorepo/shared';
import type { PriceOption } from '../../types';
import { priceOptionDisplay, priceOptionHasDuration } from '../../utils/price-display';

interface DurationSelectorProps {
  prices: PriceOption[];
  selectedPrice: PriceOption;
  onChange: (price: PriceOption) => void;
  /** Libellés (fournis par le contexte booking OU explicitement pour réutilisation hors booking). */
  labels: { duration_label: string; price_from: string; on_quote: string; free: string; unavailable: string };
  formatPrice: (price: number) => string;
}

export function DurationSelector({ prices, selectedPrice, onChange, labels, formatPrice }: DurationSelectorProps) {
  if (prices.length <= 1) return null;

  return (
    <div className="bt-duration">
      <span className="bt-duration__label">{labels.duration_label}</span>
      <div className="bt-duration__options">
        {prices.map((p, i) => {
          const isSelected = p.duration_minutes === selectedPrice.duration_minutes && p.price === selectedPrice.price;
          /* Formule sans aucun créneau : GRISÉE, jamais retirée. Même doctrine que les lieux, les
             membres et les jours du calendrier — on montre l'offre du pro et on dit pourquoi elle
             n'est pas prenable. La retirer créait deux listes divergentes (l'en-tête annonçait un
             tarif absent du sélecteur) et un état « prestation réservable, aucune formule
             sélectionnable » d'où sortait un bouton actif et muet.
             `is_bookable` absent (carte cadeau, catalogue public) ⇒ jamais grisée. */
          const isUnavailable = !isAvailablePriceOption(p);
          // Durée affichée seulement si la ligne en porte une (sinon « vitrine / sur devis »).
          const durationStr = priceOptionHasDuration(p) ? formatDurationFromMinutes(p.duration_minutes ?? 0) : '';
          const priceStr = priceOptionDisplay(p.price_type, p.price_string ?? formatPrice(p.price), labels);

          return (
            <button
              key={i}
              type="button"
              disabled={isUnavailable}
              aria-disabled={isUnavailable}
              className={`bt-duration__option ${isSelected ? 'bt-duration__option--selected' : ''} ${
                isUnavailable ? 'bt-duration__option--unavailable' : ''
              }`}
              onClick={() => {
                if (isUnavailable) return;
                onChange(p);
              }}
            >
              {p.label && <span className="bt-duration__option-label">{p.label}</span>}
              {durationStr && <span className="bt-duration__option-duration">{durationStr}</span>}
              {priceStr && <span className="bt-duration__option-price">{priceStr}</span>}
              {isUnavailable && <span className="bt-duration__option-unavailable">{labels.unavailable}</span>}
            </button>
          );
        })}
      </div>
    </div>
  );
}
